import React, { useEffect, useState } from 'react';
import { 
  SparklesIcon, 
  XMarkIcon, 
  PlayIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components';

export type AIOperationStatus = 'idle' | 'preparing' | 'processing' | 'finalizing' | 'complete' | 'error';

export interface AIOperationState {
  status: AIOperationStatus;
  progress?: number;
  currentStep?: string;
  estimatedTime?: number;
  error?: string;
  canCancel?: boolean;
  canRetry?: boolean;
  canContinueInBackground?: boolean;
}

export interface AIOperationFeedbackProps {
  operation: string;
  state: AIOperationState;
  onCancel?: () => void;
  onRetry?: () => void;
  onContinueInBackground?: () => void;
  onDismiss?: () => void;
  className?: string;
}

const STATUS_CONFIG = {
  idle: {
    icon: SparklesIcon,
    color: 'gray',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    textColor: 'text-gray-700',
    iconColor: 'text-gray-500'
  },
  preparing: {
    icon: ClockIcon,
    color: 'blue',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    iconColor: 'text-blue-600'
  },
  processing: {
    icon: SparklesIcon,
    color: 'purple',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-800',
    iconColor: 'text-purple-600'
  },
  finalizing: {
    icon: ClockIcon,
    color: 'indigo',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    textColor: 'text-indigo-800',
    iconColor: 'text-indigo-600'
  },
  complete: {
    icon: CheckCircleIcon,
    color: 'green',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    iconColor: 'text-green-600'
  },
  error: {
    icon: ExclamationTriangleIcon,
    color: 'red',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    iconColor: 'text-red-600'
  }
};

const STEP_MESSAGES = {
  preparing: [
    'Preparando solicitação...',
    'Configurando parâmetros...',
    'Inicializando IA...'
  ],
  processing: [
    'Analisando conteúdo...',
    'Gerando estruturas...',
    'Aplicando conhecimento especializado...',
    'Refinando resultados...',
    'Organizando informações...'
  ],
  finalizing: [
    'Finalizando processamento...',
    'Validando resultados...',
    'Preparando resposta...'
  ]
};

export default function AIOperationFeedback({
  operation,
  state,
  onCancel,
  onRetry,
  onContinueInBackground,
  onDismiss,
  className = ''
}: AIOperationFeedbackProps) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const config = STATUS_CONFIG[state.status];
  const Icon = config.icon;

  // Timer para tempo decorrido
  useEffect(() => {
    if (['preparing', 'processing', 'finalizing'].includes(state.status)) {
      const interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setElapsedTime(0);
    }
  }, [state.status]);

  // Rotação de mensagens de etapa
  useEffect(() => {
    if (state.status === 'processing' || state.status === 'preparing' || state.status === 'finalizing') {
      const steps = STEP_MESSAGES[state.status];
      const interval = setInterval(() => {
        setCurrentStepIndex(prev => (prev + 1) % steps.length);
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [state.status]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressWidth = () => {
    if (state.progress !== undefined) {
      return `${Math.max(5, Math.min(100, state.progress))}%`;
    }
    
    // Progress automático baseado no status
    switch (state.status) {
      case 'preparing': return '15%';
      case 'processing': return '65%';
      case 'finalizing': return '90%';
      case 'complete': return '100%';
      default: return '0%';
    }
  };

  const getCurrentStepMessage = () => {
    if (state.currentStep) return state.currentStep;
    
    const steps = STEP_MESSAGES[state.status as keyof typeof STEP_MESSAGES];
    if (steps) {
      return steps[currentStepIndex] || steps[0];
    }
    
    return '';
  };

  if (state.status === 'idle') return null;

  return (
    <div className={`rounded-lg border p-4 ${config.bgColor} ${config.borderColor} ${className}`}>
      <div className="flex items-start gap-3">
        {/* Ícone com animação */}
        <div className="flex-shrink-0 mt-1">
          {['preparing', 'processing', 'finalizing'].includes(state.status) ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current opacity-70"></div>
          ) : (
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          )}
        </div>

        {/* Conteúdo principal */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h4 className={`text-sm font-medium ${config.textColor}`}>
              {operation}
            </h4>
            {onDismiss && (state.status === 'complete' || state.status === 'error') && (
              <button
                onClick={onDismiss}
                className={`p-1 hover:bg-white hover:bg-opacity-50 rounded ${config.iconColor}`}
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status e tempo */}
          <div className="flex items-center justify-between mb-3">
            <p className={`text-sm ${config.textColor} opacity-90`}>
              {state.status === 'error' && state.error 
                ? state.error
                : getCurrentStepMessage()
              }
            </p>
            {elapsedTime > 0 && (
              <span className={`text-xs ${config.textColor} opacity-75 font-mono`}>
                {formatTime(elapsedTime)}
                {state.estimatedTime && (
                  <span className="ml-1">
                    / ~{formatTime(state.estimatedTime)}
                  </span>
                )}
              </span>
            )}
          </div>

          {/* Barra de progresso */}
          {['preparing', 'processing', 'finalizing', 'complete'].includes(state.status) && (
            <div className="mb-3">
              <div className="w-full bg-white bg-opacity-50 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-1000 ease-out ${
                    state.status === 'complete' ? 'bg-green-500' : `bg-${config.color}-500`
                  }`}
                  style={{ width: getProgressWidth() }}
                />
              </div>
              {state.progress !== undefined && (
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${config.textColor} opacity-75`}>
                    Progresso
                  </span>
                  <span className={`text-xs ${config.textColor} opacity-75 font-mono`}>
                    {Math.round(state.progress)}%
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Ações */}
          {(state.canCancel || state.canRetry || state.canContinueInBackground) && (
            <div className="flex gap-2 mt-3">
              {state.canCancel && ['preparing', 'processing', 'finalizing'].includes(state.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCancel}
                  className={`text-xs border-${config.color}-300 text-${config.color}-700 hover:bg-${config.color}-100`}
                >
                  Cancelar
                </Button>
              )}
              
              {state.canContinueInBackground && ['processing'].includes(state.status) && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onContinueInBackground}
                  className={`text-xs border-${config.color}-300 text-${config.color}-700 hover:bg-${config.color}-100`}
                  leftIcon={<PlayIcon className="w-3 h-3" />}
                >
                  Continuar em 2º plano
                </Button>
              )}
              
              {state.canRetry && state.status === 'error' && (
                <Button
                  size="sm"
                  onClick={onRetry}
                  className={`text-xs bg-${config.color}-600 hover:bg-${config.color}-700`}
                >
                  Tentar Novamente
                </Button>
              )}
            </div>
          )}

          {/* Dica para operações longas */}
          {elapsedTime > 30 && ['processing'].includes(state.status) && (
            <div className={`mt-3 text-xs ${config.textColor} opacity-75`}>
              💡 Operações complexas podem levar mais tempo. Você pode continuar editando outras partes do projeto.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}